import { useState } from 'react'
import {
  CloseButton,
  Button,
  Input,
  InputGroup,
  Table,
  Text,
  Badge,
  HStack,
  VStack,
  Spinner,
  Box,
  Switch,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiSearch } from 'react-icons/fi'
import { useWorksiteSearch, useLinkWorksiteToBusiness, useBusinessDetail } from '../../hooks/useBusiness'
import type { UnionCoverageOverride } from '../../types/business'

interface LinkExistingWorksiteModalProps {
  isOpen: boolean
  onClose: () => void
  businessId: string
}

export function LinkExistingWorksiteModal({ isOpen, onClose, businessId }: LinkExistingWorksiteModalProps) {
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [excludedUnions, setExcludedUnions] = useState<Record<string, boolean>>({})

  const { data: businessDetail } = useBusinessDetail(businessId)
  const hasUnions = (businessDetail?.unionAffiliations.length ?? 0) > 0

  const { data, isLoading } = useWorksiteSearch(
    { search: searchTerm || undefined, unparentedOnly: true, page: 1, pageSize: 20 },
    { enabled: isOpen },
  )

  const linkMutation = useLinkWorksiteToBusiness()

  const handleSearch = () => {
    setSearchTerm(searchInput)
  }

  const getUnionOverrides = (): UnionCoverageOverride[] | undefined => {
    if (!hasUnions || !businessDetail) return undefined
    return businessDetail.unionAffiliations.map(ua => ({
      unionId: ua.unionId,
      isExcluded: !!excludedUnions[ua.unionId],
    }))
  }

  const handleLink = async (worksiteId: string, worksiteLabel: string) => {
    try {
      await linkMutation.mutateAsync({ worksiteId, businessId, unionOverrides: getUnionOverrides() })
      toaster.create({ title: `Linked "${worksiteLabel}"`, type: 'success', duration: 3000 })
    } catch {
      toaster.create({ title: 'Failed to link worksite', type: 'error', duration: 3000 })
    }
  }

  return (
    <Dialog.Root open={isOpen} size='xl' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Add Existing Worksite</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={4} align="stretch">
                {/* Union coverage toggles */}
                {hasUnions && businessDetail && (
                  <>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>Union Coverage</Text>
                      <Text fontSize="xs" color="gray.500" mb={3}>
                        Set coverage for linked worksites:
                      </Text>
                      <VStack gap={2} align="stretch">
                        {businessDetail.unionAffiliations.map(ua => (
                          <HStack key={ua.unionId} justify="space-between" px={3} py={2} bg="gray.50" borderRadius="md">
                            <HStack gap={2}>
                              <Text fontSize="sm" fontWeight="medium">{ua.unionName}</Text>
                              {ua.localNumber && <Badge fontSize="xs" colorPalette="purple">Local {ua.localNumber}</Badge>}
                            </HStack>
                            <HStack gap={2}>
                              <Text fontSize="xs" color={excludedUnions[ua.unionId] ? 'red.500' : 'green.600'} fontWeight="medium">
                                {excludedUnions[ua.unionId] ? 'Excluded' : 'Covered'}
                              </Text>
                              <Switch.Root
                                size="sm"
                                checked={!excludedUnions[ua.unionId]}
                                onCheckedChange={() => setExcludedUnions(prev => ({
                                  ...prev,
                                  [ua.unionId]: !prev[ua.unionId],
                                }))}
                              >
                                <Switch.HiddenInput />
                                <Switch.Control>
                                  <Switch.Thumb />
                                </Switch.Control>
                              </Switch.Root>
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                    <Separator />
                  </>
                )}

                <HStack gap={2}>
                  <InputGroup startElement={<FiSearch color="gray" />}>
                    <Input
                      size="sm"
                      placeholder="Search unassigned worksites..."
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSearch() }}
                    />
                  </InputGroup>
                  <Button size="sm" colorPalette="brand" onClick={handleSearch}>Search</Button>
                </HStack>

                {isLoading && (
                  <VStack py={6}>
                    <Spinner size="md" color="accent" />
                  </VStack>
                )}

                {!isLoading && data && data.items.length === 0 && (
                  <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
                    No unassigned worksites found.{searchTerm ? ' Try a different search.' : ''}
                  </Text>
                )}

                {!isLoading && data && data.items.length > 0 && (
                  <Table.Root size="sm">
                    <Table.Header bg="gray.50">
                      <Table.Row>
                        <Table.ColumnHeader>Name</Table.ColumnHeader>
                        <Table.ColumnHeader>Type</Table.ColumnHeader>
                        <Table.ColumnHeader>Location</Table.ColumnHeader>
                        <Table.ColumnHeader w="80px"></Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.items.map(ws => (
                        <Table.Row key={ws.id}>
                          <Table.Cell><Text fontWeight="medium" fontSize="sm">{ws.label}</Text></Table.Cell>
                          <Table.Cell>{ws.type && <Badge bg="info.subtle" color="info.fg" fontSize="xs">{ws.type}</Badge>}</Table.Cell>
                          <Table.Cell>
                            <Text fontSize="sm" color="gray.600">
                              {[ws.city, ws.state].filter(Boolean).join(', ') || '---'}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              size="xs"
                              colorPalette="brand"
                              loading={linkMutation.isPending}
                              onClick={() => handleLink(ws.id, ws.label)}
                            >
                              Link
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                )}

                {!isLoading && data && data.totalCount > 20 && (
                  <Text fontSize="xs" color="gray.400" textAlign="center">
                    Showing first 20 of {data.totalCount} results. Use search to narrow down.
                  </Text>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button size="sm" onClick={onClose}>Done</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
